import type { MouseEvent } from 'react';
import type { RigaRiquadro, Riquadro, TipoLink, TipoRiquadro, ValoreRiquadro } from '@masamune/shared';
import type { IconName } from '../../components/ui/Icon';
import { euro } from '../../lib/utils';
import { confirm } from '../../stores/confirm.store';

/**
 * I riquadri coi numeri, le righe con link e le conversazioni della chat di MUNE (Fase 2,
 * fetta 2B «Chiedi», 29/10/2026; os-planning/ai/fase-2-progetto.md, «I riquadri»,
 * «Interfaccia» e «Parole a schermo»): come si scrivono i valori, quali icone, che parole.
 * Stanno qui e non in RiquadroChat.tsx, RigheLink.tsx e ConversazioniVista.tsx: un .tsx che
 * esporta anche funzioni o costanti rompe il Fast Refresh (schermo bianco).
 *
 * I numeri arrivano già calcolati dal server, coi dati della rotta (evento `riquadro`): qui
 * si scrivono e basta, mai si sommano o si ricalcolano. Il riquadro è un'istantanea del turno
 * (decisione 12): riaprendo una conversazione si vede uguale, senza rileggere niente.
 */

// ── Il giorno: «gio 29/10» ───────────────────────────────────────────────────

const SETTIMANA_UTC = new Intl.DateTimeFormat('it-IT', { weekday: 'short', timeZone: 'UTC' });
const ANNO_ROMA = new Intl.DateTimeFormat('it-IT', { year: 'numeric', timeZone: 'Europe/Rome' });

/** L'anno di adesso a Roma. */
export const annoDiRoma = (adesso: Date = new Date()): number => Number(ANNO_ROMA.format(adesso));

/**
 * «gio 29/10» da 'YYYY-MM-DD'. Letto in UTC come giornoScadenza di ai.azioni.regole.ts: il
 * giorno è già quello di Roma (lo sceglie il server) e le scadenze delle task stanno a
 * mezzanotte UTC, quindi nel fuso del browser a ovest di Greenwich slitterebbe di uno.
 * FMT_GIORNO di AnteprimaTask.tsx scrive «gio 29 ott»: nei riquadri il formato corto delle
 * scadenze, come lo scrive Lorenzo. L'anno solo se non è quello di adesso («lun 15/12/2025»),
 * come giornoCorto di registroApi.ts: su una task scaduta l'anno scorso, o su una rata del
 * 2027, il giorno senza l'anno ingannerebbe.
 */
export function giornoRiquadro(ymd: string, anno: number = annoDiRoma()): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(ymd);
  if (!m) return ymd;
  const a = Number(m[1]);
  const mese = Number(m[2]);
  const g = Number(m[3]);
  const d = new Date(Date.UTC(a, mese - 1, g));
  if (Number.isNaN(d.getTime())) return ymd;
  const settimana = SETTIMANA_UTC.format(d).replace(/\./g, '');
  return `${settimana} ${g}/${mese}${a !== anno ? `/${a}` : ''}`;
}

// ── I valori ─────────────────────────────────────────────────────────────────

// Raggruppate anche le quattro cifre («1.234»): lo spiega fmtEur in lib/utils.ts.
const NUMERO = new Intl.NumberFormat('it-IT', { maximumFractionDigits: 2, useGrouping: true });
// «23,5%», come il conto del progetto (ContoTab.tsx scrive il numero del server con la virgola).
const PERCENTUALE = new Intl.NumberFormat('it-IT', { maximumFractionDigits: 2, useGrouping: true });

/**
 * Un valore del riquadro scritto per le persone. Gli importi coi centesimi come le pagine
 * Banca, Fatture e Calendario (`euro(v, true)`); senza, dove lo dice `conCentesimi`. Un
 * valore vuoto è una cella vuota: niente trattino, che nella riga del telefono, unito agli
 * altri da « · », si leggerebbe come un dato.
 */
export function formattaValore(v: ValoreRiquadro, opzioni: { centesimi?: boolean } = {}): string {
  if (!v || typeof v !== 'object') return '';
  switch (v.t) {
    case 'euro':
      return euro(v.v, opzioni.centesimi ?? true);
    case 'numero': {
      const n = Number(v.v);
      return Number.isFinite(n) ? NUMERO.format(n) : '';
    }
    case 'pct': {
      const n = Number(v.v);
      return Number.isFinite(n) ? `${PERCENTUALE.format(n)}%` : '';
    }
    case 'giorno':
      return giornoRiquadro(String(v.v));
    case 'testo':
    case 'stato':
      return String(v.v ?? '');
    case 'vuoto':
      return '';
    default:
      return '';
  }
}

/**
 * I centesimi: sì, come le pagine Banca, Fatture e Calendario; no nella Situazione e nei mesi
 * dell'incassato, all'euro come la pagina Situazione (fase-2-progetto.md, «I riquadri»). I
 * totali dell'incassato restano coi centesimi: sono quelli della rotta, al centesimo.
 */
export function conCentesimi(tipo: TipoRiquadro, dove: 'righe' | 'totali'): boolean {
  if (tipo === 'situazione') return false;
  if (tipo === 'incassato') return dove === 'totali';
  return true;
}

/** Importi, numeri e percentuali: allineati a destra, con le cifre tabellari. */
export function eNumero(v: ValoreRiquadro | undefined): boolean {
  return !!v && (v.t === 'euro' || v.t === 'numero' || v.t === 'pct');
}

/**
 * Uno stato che è un importo scritto dal server (29/10/2026, ai.riquadri.ts): il saldo sotto
 * zero della Situazione («−€ 1.200», rosso) e un movimento in attesa («−€ 49,90 in attesa»,
 * grigio). Sta nella colonna degli importi, anche quando è l'unica riga.
 */
export function statoImporto(v: ValoreRiquadro | undefined): boolean {
  return !!v && v.t === 'stato' && /^[−-]?€/.test(String(v.v ?? '').trim());
}

/** Un importo, anche scritto dal server dentro uno stato: va nella colonna degli importi. */
export const eImporto = (v: ValoreRiquadro | undefined): boolean => !!v && (v.t === 'euro' || statoImporto(v));

export type TonoStato = 'ok' | 'warn' | 'alert' | 'mute';

// ── La forma del riquadro ────────────────────────────────────────────────────

/**
 * Un riquadro salvato in AiMessage.shown si rilegge com'era quando è nato, anche settimane
 * dopo: un campo che allora non c'era diventa vuoto invece di rompere la conversazione
 * riaperta (29/10/2026). I link passano da `linkInterno`: da qui in poi sono solo pagine
 * del gestionale, al computer e al telefono.
 */
export function riquadroCompleto(r: Riquadro): Riquadro {
  return {
    ...r,
    titolo: typeof r.titolo === 'string' ? r.titolo : '',
    colonne: Array.isArray(r.colonne) ? r.colonne.map((c) => (typeof c === 'string' ? c : '')) : [],
    righe: Array.isArray(r.righe)
      ? r.righe.map((x): RigaRiquadro => ({ celle: Array.isArray(x?.celle) ? x.celle : [], link: linkInterno(x?.link) }))
      : [],
    altre: Number.isFinite(r.altre) ? r.altre : 0,
    totali: Array.isArray(r.totali) ? r.totali.filter((t) => t && typeof t.etichetta === 'string' && t.valore) : [],
    note: Array.isArray(r.note) ? r.note.filter((n) => typeof n === 'string' && n.trim() !== '') : [],
    fonte: r.fonte && typeof r.fonte.testo === 'string' ? { testo: r.fonte.testo, problema: !!r.fonte.problema } : { testo: '', problema: false },
    apri: r.apri && typeof r.apri.testo === 'string' && linkInterno(r.apri.href) ? r.apri : null,
  };
}

/** Quante colonne ha la tabella: le intestazioni o la riga più lunga, se ne ha di più. */
export function quanteColonne(r: Riquadro): number {
  return r.righe.reduce((n, x) => Math.max(n, x.celle.length), r.colonne.length);
}

/**
 * Le colonne dei numeri: basta una riga con un importo, un numero o una percentuale. Nella
 * stessa colonna una cella di stato (il saldo sotto zero della Situazione, `alert`) resta
 * allineata con le altre e si scrive come loro, colorata, invece che in una pastiglia.
 */
export function colonneNumeriche(r: Riquadro): boolean[] {
  return Array.from({ length: quanteColonne(r) }, (_, i) => r.righe.some((x) => eNumero(x.celle[i]) || statoImporto(x.celle[i])));
}

// I riquadri che elencano cose al maschile: mesi, conti, movimenti, clienti, abbonamenti.
const RIGHE_AL_MASCHILE: ReadonlySet<TipoRiquadro> = new Set<TipoRiquadro>(['situazione', 'banca', 'movimenti', 'crediti', 'incassato', 'abbonamenti']);

/**
 * «e altre 4» sotto le righe, le righe che il riquadro non mostra. Al maschile dove le righe
 * sono mesi, conti, movimenti, clienti, abbonamenti o gli eventi di un abbonamento («e altri
 * 4 movimenti»); con una sola «e un'altra», non «e altre 1».
 */
export function testoAltre(r: Riquadro): string | null {
  const n = Math.floor(r.altre);
  if (!(n > 0)) return null;
  // Il pagamento di una fattura elenca le rate (colonne Rata · Importo · Stato · Come),
  // quello di un abbonamento gli eventi (Giorno · Cosa).
  const maschile = RIGHE_AL_MASCHILE.has(r.tipo) || (r.tipo === 'pagamento' && r.colonne[0] !== 'Rata');
  if (n === 1) return maschile ? 'e un altro' : "e un'altra";
  return `e ${maschile ? 'altri' : 'altre'} ${n}`;
}

// Le note dei riquadri vuoti le manda il server; queste solo se non ne manda nessuna.
const NOTA_VUOTO: Partial<Record<TipoRiquadro, string>> = {
  task: 'Nessuna task trovata',
  trattative: 'Nessuna trattativa trovata',
  movimenti: 'Nessun movimento trovato',
  incassi: 'Nessuna fattura trovata',
  crediti: 'Nessuna fattura da incassare',
  incassato: 'Nessun incasso in questo periodo',
  scadenze: 'Nessuna scadenza in questi giorni',
  abbonamenti: 'Nessun abbonamento trovato',
};

/** Le note sotto il riquadro: quelle del server; un riquadro senza righe e senza note dice che non ha trovato niente. */
export function noteDaMostrare(r: Riquadro): string[] {
  if (r.note.length || r.righe.length) return r.note;
  return [NOTA_VUOTO[r.tipo] ?? 'Nessun dato trovato'];
}

// ── Il riquadro al telefono ──────────────────────────────────────────────────

export interface PezzoRiga {
  testo: string;
  /** Solo per le celle di stato: il colore. */
  tono: TonoStato | null;
}

export interface RigaTelefono {
  titolo: string;
  /** Le altre celle, nella riga sotto, unite da « · ». */
  sotto: PezzoRiga[];
  /** L'ultima colonna in euro, a destra. */
  coda: PezzoRiga | null;
  /** Il nome della colonna a destra, sotto la cifra, quando le colonne in euro sono più d'una. */
  nomeCoda: string | null;
  link: string | null;
}

/** «Saldo a fine mese» → «saldo a fine mese»; le sigle («IVA») restano come sono. */
function minuscola(s: string): string {
  if (s.length > 1 && /[A-ZÀ-Ý]/.test(s.charAt(1))) return s;
  return s.charAt(0).toLowerCase() + s.slice(1);
}

/**
 * Al telefono un numero, un giorno o un'ora nella riga sotto porta il nome della sua colonna
 * (29/10/2026): «Attivo · mar 3/11» accanto a «è passato?» si leggeva come il giorno del
 * pagamento, ed era il rinnovo. «Giorno» no: direbbe la stessa cosa due volte.
 */
function conNomeColonna(v: ValoreRiquadro | undefined, colonna: string): boolean {
  if (!v) return false;
  if (eNumero(v)) return true;
  if (v.t === 'giorno') return colonna !== 'Giorno';
  return v.t === 'testo' && /\b\d{1,2}:\d{2}\b/.test(v.v);
}

/**
 * Le righe del riquadro al telefono, una `MRow` per riga (fase-2-progetto.md, «Telefono»):
 * titolo la prima cella, a destra l'ultima colonna in euro, le altre celle nella riga sotto
 * unite da « · ». Al telefono le intestazioni delle colonne non ci sono, quindi un importo
 * nella riga sotto porta il nome della sua colonna («Scaduto € 1.220,00»), e quello a destra
 * lo porta sotto di sé quando le colonne in euro sono più d'una («da incassare»): due cifre
 * vicine senza nome non dicono quale è quale (Lorenzo: ogni etichetta nomina la cosa).
 */
export function righeTelefono(r: Riquadro): RigaTelefono[] {
  const n = quanteColonne(r);
  const inEuro = Array.from({ length: n }, (_, i) => r.righe.some((x) => eImporto(x.celle[i])));
  const ultima = inEuro.lastIndexOf(true);
  // La prima cella è il titolo: a destra va una colonna dopo la prima.
  const iCoda = ultima > 0 ? ultima : -1;
  const colonneInEuro = inEuro.filter(Boolean).length;
  const centesimi = conCentesimi(r.tipo, 'righe');
  const nomeCoda = iCoda > 0 && colonneInEuro > 1 && r.colonne[iCoda] ? minuscola(r.colonne[iCoda]!) : null;

  return r.righe.map((riga) => {
    const pezzo = (i: number): PezzoRiga | null => {
      const v = riga.celle[i];
      if (!v) return null;
      const testo = formattaValore(v, { centesimi });
      if (!testo) return null;
      return { testo, tono: v.t === 'stato' ? v.tono : null };
    };
    const sotto: PezzoRiga[] = [];
    for (let i = 1; i < n; i++) {
      if (i === iCoda) continue;
      const p = pezzo(i);
      if (!p) continue;
      const colonna = r.colonne[i];
      sotto.push(colonna && conNomeColonna(riga.celle[i], colonna) ? { ...p, testo: `${colonna} ${p.testo}` } : p);
    }
    // «Unica» o «1 di 3» da soli non dicono cosa sono: al telefono l'intestazione «Rata» non c'è.
    const titolo = pezzo(0)?.testo ?? '';
    return {
      titolo: r.colonne[0] === 'Rata' && titolo ? `Rata ${minuscola(titolo)}` : titolo,
      sotto,
      coda: iCoda > 0 ? pezzo(iCoda) : null,
      nomeCoda,
      link: linkInterno(riga.link),
    };
  });
}

// ── I link ───────────────────────────────────────────────────────────────────

/**
 * Solo pagine del gestionale («/tasks/c…», «/admin/calendario»). Gli indirizzi li costruisce
 * il server dagli id letti, mai il modello; qui si scarta comunque tutto il resto (indirizzi
 * esterni, «//», «javascript:»): un link nella chat apre solo pagine nostre.
 */
export function linkInterno(href: string | null | undefined): string | null {
  if (typeof href !== 'string') return null;
  return /^\/(?![/\\])[^\s\\]*$/.test(href) ? href : null;
}

/** Solo «tel:» e «mailto:», per Chiama e Scrivi dei referenti. */
export function linkContatto(href: string | null | undefined): string | null {
  if (typeof href !== 'string') return null;
  return /^(tel|mailto):\S/i.test(href) ? href : null;
}

/** Il numero o l'indirizzo di un «tel:» o di un «mailto:»: si legge al passaggio del mouse. */
export function recapitoDi(href: string): string {
  const senza = href.replace(/^(tel|mailto):/i, '').split('?')[0] ?? '';
  try {
    return decodeURIComponent(senza);
  } catch {
    return senza;
  }
}

/**
 * Il clic su una riga o su un link della chat apre la pagina con `onLink`: lo decide il
 * pannello (al computer resta aperto, al telefono si chiude). Ctrl, Cmd, Maiusc o Alt sul
 * link lasciano fare al browser, che la apre in un'altra scheda come un link qualsiasi; un
 * testo appena selezionato dentro la riga (un importo da copiare) non apre niente.
 */
export function clicCheApre(href: string, onLink: (href: string) => void) {
  return (e: MouseEvent<HTMLElement>) => {
    if (e.defaultPrevented || e.button !== 0) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    const selezione = window.getSelection();
    if (selezione && !selezione.isCollapsed && selezione.anchorNode && e.currentTarget.contains(selezione.anchorNode)) return;
    onLink(href);
  };
}

const ICONA_LINK: Record<TipoLink, IconName> = {
  task: 'tasks',
  cliente: 'building',
  // Come nel menu: il cubo è il progetto.
  progetto: 'cube',
  trattativa: 'handshake',
  fattura: 'invoice',
  abbonamento: 'repeat',
  referente: 'user',
};

/** L'icona di una riga con link (fase-2-progetto.md, «Righe con link»). */
export function iconaLink(t: TipoLink): IconName {
  return ICONA_LINK[t] ?? 'link';
}

// ── Le conversazioni ─────────────────────────────────────────────────────────

/** Le parole della vista «Conversazioni» (Parole a schermo). */
export const PAROLE_CONVERSAZIONI = {
  titolo: 'Conversazioni',
  restano: 'Restano 30 giorni',
  vuota: 'Nessuna conversazione negli ultimi 30 giorni.',
  carico: 'Carico le conversazioni…',
  errore: 'Le conversazioni non si sono caricate: riprova tra un momento.',
  aperta: 'aperta adesso',
  senzaTitolo: 'Conversazione senza titolo',
} as const;

/**
 * La conferma prima di eliminare una conversazione, con le parole del progetto («Eliminare»).
 * La usa la vista «Conversazioni»; il pannello la può usare anche per «Elimina questa
 * conversazione» del menu ⋯, così le parole restano le stesse.
 */
export function confermaEliminaConversazione(): Promise<boolean> {
  return confirm({
    title: 'Eliminare questa conversazione?',
    // Con la 2F (29/10/2026) le foto e i PDF mandati vanno via con la conversazione, a cascata.
    body: 'Sparisce per sempre, con le foto e i PDF che ci hai mandato. Le modifiche fatte da MUNE restano nel registro.',
    confirmLabel: 'Elimina',
    cancelLabel: "Lascia com'è",
    icon: 'trash',
    danger: true,
  });
}
